import { useQuery, UseQueryResult } from "@tanstack/react-query";
import getConfig from "next/config";
import { createSelector } from "reselect";

const { publicRuntimeConfig } = getConfig();
const {
    CONTENTFUL_SPACE_ID,
    CONTENTFUL_ENVIRONMENT,
    CONTENTFUL_ACCESS_TOKEN,
    CONTENTFUL_BASE_URL
} = publicRuntimeConfig;

export type MenuVariantContentFragment = {
    variantName: string;
    variantCode: string;
    variantDescription: string;
    variantImage: {
        title: string;
        description: string;
        url: string;
    };
};

export type MenuVariantContentResponse = {
    data:{
        menuVariantCollection: {
            items: MenuVariantContentFragment[];
        };
    };
}

export type MenuVariantContentData = {
    variants: Record<string, MenuVariantContentFragment | undefined>;
}

export type MenuVariantContentQueryResult = UseQueryResult<MenuVariantContentData, Error>;

const query = `
    query GetMenuContent {
    menuVariantCollection {
        items {
        ...menuVariantContent
        }
    }
    }

    fragment menuVariantContent on MenuVariant {
    variantName
    variantCode
    variantDescription
    variantImage {
        title
        description
        url
    }  
    }`;

const mapByKey = <T, K extends keyof T>(items: T[], keyName: K): Record<string, T> => {
    const initial: Record<string, T> = {};
    return items.reduce((acc: Record<string, T>, item: T) => {
        const key = item[keyName];
        if (typeof key === 'string') {
            acc[key] = item;
        }
        return acc;
    }, initial);
};

export type ContentMap = {
    variants: Record<string, MenuVariantContentFragment | undefined>;
};

const contentMapSelector = createSelector(
    (response: MenuVariantContentResponse) => response?.data,
    (data) => {
        const variants = mapByKey(
            (data?.menuVariantCollection.items) ?? [],
            'variantCode'
        );

        return {
            variants
        };
    }
);

const fetchMenuVariantContent = async (): Promise<MenuVariantContentResponse> => {
    const response = await fetch(
        `${CONTENTFUL_BASE_URL}/spaces/${CONTENTFUL_SPACE_ID}/environments/${CONTENTFUL_ENVIRONMENT}`,
        {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${CONTENTFUL_ACCESS_TOKEN}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ query })
        }
    );

    const data: MenuVariantContentResponse = await response.json();
    return data;
};

export function useMenuVariantContent(): MenuVariantContentQueryResult {

    return useQuery({
        queryKey: ['menuVariantContent'],
        queryFn: () => fetchMenuVariantContent(),
        select: contentMapSelector
    })
};